import { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Building2, CalendarPlus, Check, GraduationCap, Loader2, PartyPopper, Send, Ticket, Users } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DatePicker from "@/components/DatePicker";
import { EVENT_MODULES, ADDRESS } from "@/lib/site";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const ICONS = { Building2, GraduationCap, PartyPopper };

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ev = useMemo(() => EVENT_MODULES.find((e) => e.id === id), [id]);
  useSeo({
    title: ev ? `${ev.title} Bookings | Cool Runnings Cape Town` : "Group Events | Cool Runnings Cape Town",
    description: ev
      ? `${ev.title} at Cool Runnings Cape Town — ${ev.tagline} Group bookings on Africa's first downhill toboggan track.`
      : undefined,
    keywords:
      "corporate team building Cape Town, school trips Cool Runnings, group bookings toboggan park, kids party venue Cape Town",
    image: ev?.img,
  });
  const [date, setDate] = useState(undefined);
  const [form, setForm] = useState({ name: "", email: "", phone: "", groupSize: "20 – 49", message: "", website: "" });
  const [sending, setSending] = useState(false);

  if (!ev) {
    return (
      <section className="flex min-h-[60vh] flex-col items-center justify-center gap-6 px-4 text-center">
        <p className="font-display text-3xl">That event has ridden off into the sunset.</p>
        <button onClick={() => navigate("/events")} className="btn-gold" data-testid="event-back-to-events-btn">
          <ArrowLeft size={15} /> Back to events
        </button>
      </section>
    );
  }

  const Icon = ICONS[ev.icon] || PartyPopper;

  const submit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please pick your preferred date on the calendar.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        enquiry_type: ev.type,
        visit_date: date.toISOString().slice(0, 10),
        message: `Group size: ${form.groupSize}. ${form.message}`.trim(),
        website: form.website,
        elapsed_ms: 30000,
      });
      toast.success(res.data?.message || "Booking request sent — the front desk will reply soon!");
      navigate(`/contact?sent=${res.data.ref}`, { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg?.replace("Value error, ", "") || "Something went wrong — please try again.");
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader
        goodgirlTitle
        pageName={ev.title}
        eyebrow="Events & groups"
        title={ev.title.toUpperCase()}
        subline={ev.tagline}
        image={ev.img}
      />

      <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6" data-testid="event-detail-section">
        <Link to="/events" className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-[#c87700] transition-colors hover:text-[#f8b818]" data-testid="event-detail-back-link">
          <ArrowLeft size={14} /> All events & groups
        </Link>

        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="booking-path-split">
          <div className="flex items-center gap-4 rounded-2xl border border-[#22c55e]/30 bg-[#22c55e]/10 p-5" data-testid="event-ride-only-card">
            <Ticket size={26} className="shrink-0 text-[#16a34a]" />
            <p className="text-sm leading-relaxed text-[#241c0d]">
              <b>Just riding? No booking needed.</b> Buy rides online and save 10%, or pay at the ticket office on
              the day. Entrance and parking are free.
            </p>
          </div>
          <div className="flex items-center gap-4 rounded-2xl border border-[#f8b818]/40 bg-[#f8b818]/10 p-5" data-testid="event-booking-reqs-card">
            <CalendarPlus size={26} className="shrink-0 text-[#c87700]" />
            <p className="text-sm leading-relaxed text-[#241c0d]">
              <b>Booking this party?</b> We confirm availability and your tables by email — just bring an alternative date.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f8b818] text-[#0a0a0b]">
                <Icon size={22} />
              </span>
              <h2 className="font-display text-3xl sm:text-4xl">THE BIG DAY, SORTED</h2>
            </div>
            <p className="mt-5 text-base leading-relaxed text-[#3d362c]">{ev.text}</p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="card-dark p-5">
                <p className="text-xs font-extrabold uppercase tracking-widest text-[#f8b818]">What's included</p>
                <ul className="mt-3 space-y-2">
                  {ev.includes.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm font-bold text-[#241c0d]/85">
                      <Check size={15} className="mt-0.5 shrink-0 text-[#c87700]" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-dark p-5">
                <p className="text-xs font-extrabold uppercase tracking-widest text-[#f8b818]">Good to know</p>
                <ul className="mt-3 space-y-2 text-sm font-bold text-[#241c0d]/85">
                  <li className="flex items-start gap-2"><Users size={15} className="mt-0.5 shrink-0 text-[#c87700]" /> Ideal for: {ev.idealFor}</li>
                  <li className="flex items-start gap-2"><CalendarPlus size={15} className="mt-0.5 shrink-0 text-[#c87700]" /> {ev.priceHint}</li>
                  <li className="flex items-start gap-2"><Building2 size={15} className="mt-0.5 shrink-0 text-[#c87700]" /> {ADDRESS}</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={submit}
            className="card-dark h-fit space-y-4 p-7"
            data-testid="event-booking-form"
          >
            <p className="font-display text-2xl text-[#f8b818]">BOOK THIS EVENT</p>
            <p className="text-sm text-[#8a7b5e]">Pick your date on the calendar and we'll confirm availability by email.</p>
            <input
              className="input-dark"
              placeholder="Your name *"
              required
              minLength={2}
              maxLength={80}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="event-name-input"
            />
            <input
              type="email"
              className="input-dark"
              placeholder="Email address *"
              required
              maxLength={120}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="event-email-input"
            />
            <input
              type="tel"
              className="input-dark"
              placeholder="Phone with country code, e.g. +27 82 123 4567"
              maxLength={16}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="event-phone-input"
            />
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">Preferred date *</p>
              <DatePicker
                value={date}
                onChange={setDate}
                placeholder="Choose your day on the calendar"
                testid="event-date-picker"
              />
            </div>
            <select
              className="input-dark"
              value={form.groupSize}
              onChange={(e) => setForm({ ...form, groupSize: e.target.value })}
              data-testid="event-group-size-select"
              aria-label="Group size"
            >
              {["10 – 19", "20 – 49", "50 – 99", "100+"].map((g) => (
                <option key={g} value={g}>{g} guests</option>
              ))}
            </select>
            <textarea
              className="input-dark min-h-24"
              placeholder="Tell us about your day — catering, age groups, anything!"
              maxLength={2000}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="event-message-textarea"
            />
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
              data-testid="event-website-hp"
            />
            <button type="submit" disabled={sending} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="event-submit-btn">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send booking request"}
            </button>
            <p className="text-center text-[11px] text-[#8a7b5e]">
              No payment now — we confirm availability and email you a quote first.
            </p>
          </motion.form>
        </div>
      </section>
    </>
  );
}
